/**
 * Wire-value formatting helpers. The admin-api wire convention is epoch ms
 * (numbers) for every timestamp — there is no Firestore Timestamp here, so this
 * replaces the old `fsutil.toDate`. Pages format numbers, never Date objects.
 */

/** Epoch ms → "YYYY-MM-DD HH:mm". Returns "—" for null/undefined/0. */
export function fmtDate(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms === 0) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Epoch ms → "YYYY-MM-DD" (date only). */
export function fmtDay(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms === 0) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Relative-time, coarse ("剛剛" / "5 分鐘前" / "3 小時前" / falls back to fmtDate). */
export function fmtRelative(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms === 0) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return fmtDate(ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '剛剛';
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} 天前`;
  return fmtDate(ms);
}

/** A decrease for display: 「−3」, but a plain 「0」 (never 「−0」). */
export function minus(n: number): string {
  return n > 0 ? `−${n}` : '0';
}

/** Always relative (剛剛 / N 分鐘前 / N 小時前 / N 天前 / N 個月前 / N 年前), for one consistent column;
 *  pair it with fmtDate in a title tooltip. */
export function fmtAgo(ms: number | null | undefined, now: number = Date.now()): string {
  if (ms === null || ms === undefined || ms === 0) return '—';
  const diff = now - ms;
  if (diff < 60_000) return '剛剛';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  if (day < 365) return `${Math.floor(day / 30)} 個月前`;
  return `${Math.floor(day / 365)} 年前`;
}

/** 「ARMR-••••-••••-7Q2K」 → 「••••-7Q2K」: only the last group, the way the board shows a bound code. */
export function shortMaskedCode(masked: string | null | undefined): string {
  const parts = (masked ?? '').split('-').map((p) => p.trim()).filter(Boolean);
  const last = parts[parts.length - 1];
  return last && !/^•+$/.test(last) ? `••••-${last}` : '••••';
}
